import { Schema, Struct } from "effect";
import { NonNegativeInt, ProjectId, ThreadId, TrimmedNonEmptyString } from "./baseSchemas";

import {
  ClientOrchestrationCommand,
  OrchestrationEvent,
  OrchestrationLoadOlderThreadHistoryInput,
  OrchestrationReplayThreadEventsInput,
  OrchestrationShellStreamItem,
  OrchestrationSubscribeShellInput,
  OrchestrationSubscribeThreadInput,
  OrchestrationThreadStreamItem,
  OrchestrationUnsubscribeShellInput,
  OrchestrationUnsubscribeThreadInput,
  ORCHESTRATION_WS_CHANNELS,
  OrchestrationGetFullThreadDiffInput,
  OrchestrationRepairStateInput,
  ORCHESTRATION_WS_METHODS,
  OrchestrationGetSnapshotInput,
  OrchestrationGetTurnDiffInput,
  OrchestrationReplayEventsInput,
  SetProjectRepoCredentialsInput,
  ValidateProjectRepoCredentialsInput,
  ClearProjectRepoCredentialsInput,
  CreateProjectFromRepositoryInput,
  StartThreadDevServerInput,
  StopThreadDevServerInput,
  UpdateThreadDevCommandInput,
  GetThreadDevServerInput,
  SetThreadDevServerVisibilityInput,
  GetThreadUserStateInput,
  MarkThreadVisitedInput,
  ThreadDevServerStateChangedPayload,
} from "./orchestration";
import {
  GitActionProgressEvent,
  GitCheckoutInput,
  GitCreateBranchInput,
  GitCreateDetachedWorktreeInput,
  GitHandoffThreadInput,
  GitPreparePullRequestThreadInput,
  GitCreateWorktreeInput,
  GitInitInput,
  GitListBranchesInput,
  GitProjectPullRequestsInput,
  GitPullInput,
  GitPullRequestRefInput,
  GitReadWorkingTreeDiffInput,
  GitRemoveWorktreeInput,
  GitRunStackedActionInput,
  GitStatusInput,
} from "./git";
import {
  TerminalClearInput,
  TerminalCloseInput,
  TerminalEvent,
  TerminalOpenInput,
  TerminalResizeInput,
  TerminalRestartInput,
  TerminalWriteInput,
} from "./terminal";
import { KeybindingRule } from "./keybindings";
import {
  ProjectListDirectoryInput,
  ProjectReadFileInput,
  ProjectSearchEntriesInput,
  ProjectWriteFileInput,
} from "./project";
import { ProjectReadConfigInput } from "./projectConfig";
import { FilePreviewGlobsInput, FilePreviewInput } from "./filePreview";
import { OpenInEditorInput } from "./editor";
import {
  ServerConfigUpdatedPayload,
  ServerProviderStatusesUpdatedPayload,
  ServerVoiceTranscriptionInput,
} from "./server";
import {
  ProviderListCommandsInput,
  ProviderGetComposerCapabilitiesInput,
  ProviderListPluginsInput,
  ProviderListModelsInput,
  ProviderListAgentsInput,
  ProviderReadPluginInput,
  ProviderListSkillsInput,
} from "./providerDiscovery";
import { ProviderCompactThreadInput } from "./provider";
import {
  WorkspaceEnsureAgentThreadTerminalInput,
  WorkspacePrepareAgentWorktreeInput,
  WorkspacePrepareThreadWorktreeInput,
  WorkspaceRunProjectScriptInput,
} from "./workspace";
import { ArchiveHookEventSchema } from "./archiveHook";
import { ProjectScriptRunEventSchema } from "./projectScriptRun";
import {
  ComposerQueueChangedPayload,
  ComposerQueueEnqueueInput,
  ComposerQueueListInput,
  ComposerQueueRemoveInput,
  ComposerQueueSendInput,
  ComposerQueueUpdateInput,
} from "./composerQueue";
import {
  LocalExecAnnounceInput,
  LocalExecControlPayload,
  LocalExecRequestPayload,
  LocalExecRespondInput,
} from "./localExec";
import {
  PullRequestActivityInput,
  PullRequestCommentInput,
  PullRequestDetailInput,
  PullRequestInvalidateInput,
  PullRequestListInput,
  PullRequestRunActionInput,
} from "./pullRequest";
import { ThreadDirectoryInput } from "./threadDirectory";

// ── WebSocket RPC Method Names ───────────────────────────────────────

export const WS_METHODS = {
  // Project registry methods
  projectsList: "projects.list",
  projectsAdd: "projects.add",
  projectsRemove: "projects.remove",
  projectsSearchEntries: "projects.searchEntries",
  projectsListDirectory: "projects.listDirectory",
  projectsWriteFile: "projects.writeFile",
  projectsReadFile: "projects.readFile",
  projectsReadConfig: "projects.readConfig",

  // File preview methods (glob-allowlisted absolute path reads)
  filesPreview: "files.preview",
  filesPreviewGlobs: "files.preview-globs",

  // Shell methods
  shellOpenInEditor: "shell.openInEditor",

  // Git methods
  gitPull: "git.pull",
  gitStatus: "git.status",
  gitProjectPullRequests: "git.projectPullRequests",
  gitReadWorkingTreeDiff: "git.readWorkingTreeDiff",
  gitRunStackedAction: "git.runStackedAction",
  gitListBranches: "git.listBranches",
  gitCreateWorktree: "git.createWorktree",
  gitCreateDetachedWorktree: "git.createDetachedWorktree",
  gitRemoveWorktree: "git.removeWorktree",
  gitCreateBranch: "git.createBranch",
  gitCheckout: "git.checkout",
  gitInit: "git.init",
  gitHandoffThread: "git.handoffThread",
  gitResolvePullRequest: "git.resolvePullRequest",
  gitPreparePullRequestThread: "git.preparePullRequestThread",

  // Terminal methods
  terminalOpen: "terminal.open",
  terminalWrite: "terminal.write",
  terminalResize: "terminal.resize",
  terminalClear: "terminal.clear",
  terminalRestart: "terminal.restart",
  terminalClose: "terminal.close",

  // Server meta
  serverPing: "server.ping",
  serverGetConfig: "server.getConfig",
  serverRefreshProviders: "server.refreshProviders",
  serverListWorktrees: "server.listWorktrees",
  serverTranscribeVoice: "server.transcribeVoice",
  serverUpsertKeybinding: "server.upsertKeybinding",

  // Workspace orchestration
  workspacePrepareAgentWorktree: "workspace.prepareAgentWorktree",
  workspacePrepareThreadWorktree: "workspace.prepareThreadWorktree",
  workspaceEnsureAgentThreadTerminal: "workspace.ensureAgentThreadTerminal",
  workspaceRunProjectScript: "workspace.runProjectScript",

  // Local execution on the user's own machine, brokered by the desktop app.
  // `announce` registers a desktop socket and the entries that machine has
  // accepted; `respond` returns the outcome of one dispatched request.
  localExecAnnounce: "localExec.announce",
  localExecRespond: "localExec.respond",

  // Provider discovery
  providerGetComposerCapabilities: "provider.getComposerCapabilities",
  providerCompactThread: "provider.compactThread",
  providerListCommands: "provider.listCommands",
  providerListSkills: "provider.listSkills",
  providerListPlugins: "provider.listPlugins",
  providerReadPlugin: "provider.readPlugin",
  providerListModels: "provider.listModels",
  providerListAgents: "provider.listAgents",

  // Pull requests (GitHub, read through the per-project encrypted token)
  pullRequestsList: "pullRequests.list",
  pullRequestsDetail: "pullRequests.detail",
  pullRequestsActivity: "pullRequests.activity",
  pullRequestsRunAction: "pullRequests.runAction",
  pullRequestsComment: "pullRequests.comment",
  pullRequestsInvalidate: "pullRequests.invalidate",

  // Composer queue (server-side queued messages, owned by their author)
  composerQueueList: "composerQueue.list",
  composerQueueEnqueue: "composerQueue.enqueue",
  composerQueueUpdate: "composerQueue.update",
  composerQueueRemove: "composerQueue.remove",
  composerQueueSend: "composerQueue.send",
} as const;

// ── Push Event Channels ──────────────────────────────────────────────

export const WS_CHANNELS = {
  gitActionProgress: "git.actionProgress",
  terminalEvent: "terminal.event",
  serverWelcome: "server.welcome",
  serverConfigUpdated: "server.configUpdated",
  serverProviderStatusesUpdated: "server.providerStatusesUpdated",
  workspaceArchiveHookStatus: "workspace.archiveHookStatus",
  workspaceProjectScriptStatus: "workspace.projectScriptStatus",
  composerQueueChanged: "composerQueue.changed",
  // Pushed to one specific desktop socket, never broadcast: `request` carries a
  // resolved argv for that machine to run, `control` kills what is still
  // running there.
  localExecRequest: "localExec.request",
  localExecControl: "localExec.control",
} as const;

// -- Tagged Union of all request body schemas ─────────────────────────

const tagRequestBody = <const Tag extends string, const Fields extends Schema.Struct.Fields>(
  tag: Tag,
  schema: Schema.Struct<Fields>,
) =>
  schema.mapFields(
    Struct.assign({ _tag: Schema.tag(tag) }),
    // PreserveChecks is safe here. No existing schema should have checks depending on the tag
    { unsafePreserveChecks: true },
  );

const WebSocketRequestBody = Schema.Union([
  // Orchestration methods
  tagRequestBody(
    ORCHESTRATION_WS_METHODS.dispatchCommand,
    Schema.Struct({ command: ClientOrchestrationCommand }),
  ),
  tagRequestBody(ORCHESTRATION_WS_METHODS.getSnapshot, OrchestrationGetSnapshotInput),
  tagRequestBody(ORCHESTRATION_WS_METHODS.repairState, OrchestrationRepairStateInput),
  tagRequestBody(ORCHESTRATION_WS_METHODS.getTurnDiff, OrchestrationGetTurnDiffInput),
  tagRequestBody(ORCHESTRATION_WS_METHODS.getFullThreadDiff, OrchestrationGetFullThreadDiffInput),
  tagRequestBody(ORCHESTRATION_WS_METHODS.replayEvents, OrchestrationReplayEventsInput),
  tagRequestBody(ORCHESTRATION_WS_METHODS.subscribeShell, OrchestrationSubscribeShellInput),
  tagRequestBody(ORCHESTRATION_WS_METHODS.unsubscribeShell, OrchestrationUnsubscribeShellInput),
  tagRequestBody(ORCHESTRATION_WS_METHODS.subscribeThread, OrchestrationSubscribeThreadInput),
  tagRequestBody(ORCHESTRATION_WS_METHODS.unsubscribeThread, OrchestrationUnsubscribeThreadInput),
  tagRequestBody(
    ORCHESTRATION_WS_METHODS.loadOlderThreadHistory,
    OrchestrationLoadOlderThreadHistoryInput,
  ),
  tagRequestBody(ORCHESTRATION_WS_METHODS.replayThreadEvents, OrchestrationReplayThreadEventsInput),
  tagRequestBody(
    ORCHESTRATION_WS_METHODS.setProjectRepoCredentials,
    SetProjectRepoCredentialsInput,
  ),
  tagRequestBody(
    ORCHESTRATION_WS_METHODS.validateProjectRepoCredentials,
    ValidateProjectRepoCredentialsInput,
  ),
  tagRequestBody(
    ORCHESTRATION_WS_METHODS.clearProjectRepoCredentials,
    ClearProjectRepoCredentialsInput,
  ),
  tagRequestBody(
    ORCHESTRATION_WS_METHODS.createProjectFromRepository,
    CreateProjectFromRepositoryInput,
  ),
  tagRequestBody(ORCHESTRATION_WS_METHODS.startThreadDevServer, StartThreadDevServerInput),
  tagRequestBody(ORCHESTRATION_WS_METHODS.stopThreadDevServer, StopThreadDevServerInput),
  tagRequestBody(ORCHESTRATION_WS_METHODS.updateThreadDevCommand, UpdateThreadDevCommandInput),
  tagRequestBody(ORCHESTRATION_WS_METHODS.getThreadDevServer, GetThreadDevServerInput),
  tagRequestBody(
    ORCHESTRATION_WS_METHODS.setThreadDevServerVisibility,
    SetThreadDevServerVisibilityInput,
  ),
  tagRequestBody(ORCHESTRATION_WS_METHODS.getThreadUserState, GetThreadUserStateInput),
  tagRequestBody(ORCHESTRATION_WS_METHODS.markThreadVisited, MarkThreadVisitedInput),
  tagRequestBody(ORCHESTRATION_WS_METHODS.listThreadDirectory, ThreadDirectoryInput),

  // Project Search
  tagRequestBody(WS_METHODS.projectsSearchEntries, ProjectSearchEntriesInput),
  tagRequestBody(WS_METHODS.projectsListDirectory, ProjectListDirectoryInput),
  tagRequestBody(WS_METHODS.projectsWriteFile, ProjectWriteFileInput),
  tagRequestBody(WS_METHODS.projectsReadFile, ProjectReadFileInput),
  tagRequestBody(WS_METHODS.projectsReadConfig, ProjectReadConfigInput),

  tagRequestBody(WS_METHODS.filesPreview, FilePreviewInput),
  tagRequestBody(WS_METHODS.filesPreviewGlobs, FilePreviewGlobsInput),

  // Shell methods
  tagRequestBody(WS_METHODS.shellOpenInEditor, OpenInEditorInput),

  // Git methods
  tagRequestBody(WS_METHODS.gitPull, GitPullInput),
  tagRequestBody(WS_METHODS.gitStatus, GitStatusInput),
  tagRequestBody(WS_METHODS.gitProjectPullRequests, GitProjectPullRequestsInput),
  tagRequestBody(WS_METHODS.gitReadWorkingTreeDiff, GitReadWorkingTreeDiffInput),
  tagRequestBody(WS_METHODS.gitRunStackedAction, GitRunStackedActionInput),
  tagRequestBody(WS_METHODS.gitListBranches, GitListBranchesInput),
  tagRequestBody(WS_METHODS.gitCreateWorktree, GitCreateWorktreeInput),
  tagRequestBody(WS_METHODS.gitCreateDetachedWorktree, GitCreateDetachedWorktreeInput),
  tagRequestBody(WS_METHODS.gitRemoveWorktree, GitRemoveWorktreeInput),
  tagRequestBody(WS_METHODS.gitCreateBranch, GitCreateBranchInput),
  tagRequestBody(WS_METHODS.gitCheckout, GitCheckoutInput),
  tagRequestBody(WS_METHODS.gitInit, GitInitInput),
  tagRequestBody(WS_METHODS.gitHandoffThread, GitHandoffThreadInput),
  tagRequestBody(WS_METHODS.gitResolvePullRequest, GitPullRequestRefInput),
  tagRequestBody(WS_METHODS.gitPreparePullRequestThread, GitPreparePullRequestThreadInput),

  // Terminal methods
  tagRequestBody(WS_METHODS.terminalOpen, TerminalOpenInput),
  tagRequestBody(WS_METHODS.terminalWrite, TerminalWriteInput),
  tagRequestBody(WS_METHODS.terminalResize, TerminalResizeInput),
  tagRequestBody(WS_METHODS.terminalClear, TerminalClearInput),
  tagRequestBody(WS_METHODS.terminalRestart, TerminalRestartInput),
  tagRequestBody(WS_METHODS.terminalClose, TerminalCloseInput),

  // Server meta
  // Liveness probe. Clients use it to tell a live socket from a zombie one —
  // mobile browsers freeze pages on screen lock without firing `close`, so a
  // socket can read as OPEN long after the connection is gone.
  tagRequestBody(WS_METHODS.serverPing, Schema.Struct({})),
  tagRequestBody(WS_METHODS.serverGetConfig, Schema.Struct({})),
  tagRequestBody(WS_METHODS.serverRefreshProviders, Schema.Struct({})),
  tagRequestBody(WS_METHODS.serverListWorktrees, Schema.Struct({})),
  tagRequestBody(WS_METHODS.serverTranscribeVoice, ServerVoiceTranscriptionInput),
  tagRequestBody(WS_METHODS.serverUpsertKeybinding, KeybindingRule),

  // Workspace orchestration
  tagRequestBody(WS_METHODS.workspacePrepareAgentWorktree, WorkspacePrepareAgentWorktreeInput),
  tagRequestBody(WS_METHODS.workspacePrepareThreadWorktree, WorkspacePrepareThreadWorktreeInput),
  tagRequestBody(
    WS_METHODS.workspaceEnsureAgentThreadTerminal,
    WorkspaceEnsureAgentThreadTerminalInput,
  ),
  tagRequestBody(WS_METHODS.workspaceRunProjectScript, WorkspaceRunProjectScriptInput),

  // Local execution
  tagRequestBody(WS_METHODS.localExecAnnounce, LocalExecAnnounceInput),
  tagRequestBody(WS_METHODS.localExecRespond, LocalExecRespondInput),

  // Provider discovery
  tagRequestBody(WS_METHODS.providerGetComposerCapabilities, ProviderGetComposerCapabilitiesInput),
  tagRequestBody(WS_METHODS.providerCompactThread, ProviderCompactThreadInput),
  tagRequestBody(WS_METHODS.providerListCommands, ProviderListCommandsInput),
  tagRequestBody(WS_METHODS.providerListSkills, ProviderListSkillsInput),
  tagRequestBody(WS_METHODS.providerListPlugins, ProviderListPluginsInput),
  tagRequestBody(WS_METHODS.providerReadPlugin, ProviderReadPluginInput),
  tagRequestBody(WS_METHODS.providerListModels, ProviderListModelsInput),
  tagRequestBody(WS_METHODS.providerListAgents, ProviderListAgentsInput),

  // Pull request methods
  tagRequestBody(WS_METHODS.pullRequestsList, PullRequestListInput),
  tagRequestBody(WS_METHODS.pullRequestsDetail, PullRequestDetailInput),
  tagRequestBody(WS_METHODS.pullRequestsActivity, PullRequestActivityInput),
  tagRequestBody(WS_METHODS.pullRequestsRunAction, PullRequestRunActionInput),
  tagRequestBody(WS_METHODS.pullRequestsComment, PullRequestCommentInput),
  tagRequestBody(WS_METHODS.pullRequestsInvalidate, PullRequestInvalidateInput),

  // Composer queue methods
  tagRequestBody(WS_METHODS.composerQueueList, ComposerQueueListInput),
  tagRequestBody(WS_METHODS.composerQueueEnqueue, ComposerQueueEnqueueInput),
  tagRequestBody(WS_METHODS.composerQueueUpdate, ComposerQueueUpdateInput),
  tagRequestBody(WS_METHODS.composerQueueRemove, ComposerQueueRemoveInput),
  tagRequestBody(WS_METHODS.composerQueueSend, ComposerQueueSendInput),
]);

export const WebSocketRequest = Schema.Struct({
  id: TrimmedNonEmptyString,
  body: WebSocketRequestBody,
});
export type WebSocketRequest = typeof WebSocketRequest.Type;

export const WebSocketResponse = Schema.Struct({
  id: TrimmedNonEmptyString,
  result: Schema.optional(Schema.Unknown),
  error: Schema.optional(
    Schema.Struct({
      message: Schema.String,
    }),
  ),
});
export type WebSocketResponse = typeof WebSocketResponse.Type;

export const WsPushSequence = NonNegativeInt;
export type WsPushSequence = typeof WsPushSequence.Type;

export const WsWelcomePayload = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  homeDir: Schema.optional(TrimmedNonEmptyString),
  projectName: TrimmedNonEmptyString,
  bootstrapProjectId: Schema.optional(ProjectId),
  bootstrapThreadId: Schema.optional(ThreadId),
});
export type WsWelcomePayload = typeof WsWelcomePayload.Type;

export interface WsPushPayloadByChannel {
  readonly [WS_CHANNELS.serverWelcome]: WsWelcomePayload;
  readonly [WS_CHANNELS.serverConfigUpdated]: typeof ServerConfigUpdatedPayload.Type;
  readonly [WS_CHANNELS.serverProviderStatusesUpdated]: typeof ServerProviderStatusesUpdatedPayload.Type;
  readonly [WS_CHANNELS.gitActionProgress]: typeof GitActionProgressEvent.Type;
  readonly [WS_CHANNELS.terminalEvent]: typeof TerminalEvent.Type;
  readonly [WS_CHANNELS.workspaceArchiveHookStatus]: typeof ArchiveHookEventSchema.Type;
  readonly [WS_CHANNELS.workspaceProjectScriptStatus]: typeof ProjectScriptRunEventSchema.Type;
  readonly [WS_CHANNELS.composerQueueChanged]: typeof ComposerQueueChangedPayload.Type;
  readonly [WS_CHANNELS.localExecRequest]: typeof LocalExecRequestPayload.Type;
  readonly [WS_CHANNELS.localExecControl]: typeof LocalExecControlPayload.Type;
  readonly [ORCHESTRATION_WS_CHANNELS.domainEvent]: OrchestrationEvent;
  readonly [ORCHESTRATION_WS_CHANNELS.shellEvent]: OrchestrationShellStreamItem;
  readonly [ORCHESTRATION_WS_CHANNELS.threadEvent]: OrchestrationThreadStreamItem;
  readonly [ORCHESTRATION_WS_CHANNELS.devServerStatusChanged]: ThreadDevServerStateChangedPayload;
}

export type WsPushChannel = keyof WsPushPayloadByChannel;
export type WsPushData<C extends WsPushChannel> = WsPushPayloadByChannel[C];

const makeWsPushSchema = <const Channel extends string, Payload extends Schema.Schema<any>>(
  channel: Channel,
  payload: Payload,
) =>
  Schema.Struct({
    type: Schema.Literal("push"),
    sequence: WsPushSequence,
    channel: Schema.Literal(channel),
    data: payload,
  });

export const WsPushServerWelcome = makeWsPushSchema(WS_CHANNELS.serverWelcome, WsWelcomePayload);
export const WsPushServerConfigUpdated = makeWsPushSchema(
  WS_CHANNELS.serverConfigUpdated,
  ServerConfigUpdatedPayload,
);
export const WsPushServerProviderStatusesUpdated = makeWsPushSchema(
  WS_CHANNELS.serverProviderStatusesUpdated,
  ServerProviderStatusesUpdatedPayload,
);
export const WsPushGitActionProgress = makeWsPushSchema(
  WS_CHANNELS.gitActionProgress,
  GitActionProgressEvent,
);
export const WsPushTerminalEvent = makeWsPushSchema(WS_CHANNELS.terminalEvent, TerminalEvent);
export const WsPushWorkspaceArchiveHookStatus = makeWsPushSchema(
  WS_CHANNELS.workspaceArchiveHookStatus,
  ArchiveHookEventSchema,
);
export const WsPushWorkspaceProjectScriptStatus = makeWsPushSchema(
  WS_CHANNELS.workspaceProjectScriptStatus,
  ProjectScriptRunEventSchema,
);
export const WsPushComposerQueueChanged = makeWsPushSchema(
  WS_CHANNELS.composerQueueChanged,
  ComposerQueueChangedPayload,
);
export const WsPushLocalExecRequest = makeWsPushSchema(
  WS_CHANNELS.localExecRequest,
  LocalExecRequestPayload,
);
export const WsPushLocalExecControl = makeWsPushSchema(
  WS_CHANNELS.localExecControl,
  LocalExecControlPayload,
);
export const WsPushOrchestrationDomainEvent = makeWsPushSchema(
  ORCHESTRATION_WS_CHANNELS.domainEvent,
  OrchestrationEvent,
);
export const WsPushOrchestrationShellEvent = makeWsPushSchema(
  ORCHESTRATION_WS_CHANNELS.shellEvent,
  OrchestrationShellStreamItem,
);
export const WsPushOrchestrationThreadEvent = makeWsPushSchema(
  ORCHESTRATION_WS_CHANNELS.threadEvent,
  OrchestrationThreadStreamItem,
);
export const WsPushOrchestrationDevServerStatusChanged = makeWsPushSchema(
  ORCHESTRATION_WS_CHANNELS.devServerStatusChanged,
  ThreadDevServerStateChangedPayload,
);

export const WsPushChannelSchema = Schema.Literals([
  WS_CHANNELS.gitActionProgress,
  WS_CHANNELS.serverWelcome,
  WS_CHANNELS.serverConfigUpdated,
  WS_CHANNELS.serverProviderStatusesUpdated,
  WS_CHANNELS.terminalEvent,
  WS_CHANNELS.workspaceArchiveHookStatus,
  WS_CHANNELS.workspaceProjectScriptStatus,
  WS_CHANNELS.composerQueueChanged,
  WS_CHANNELS.localExecRequest,
  WS_CHANNELS.localExecControl,
  ORCHESTRATION_WS_CHANNELS.domainEvent,
  ORCHESTRATION_WS_CHANNELS.shellEvent,
  ORCHESTRATION_WS_CHANNELS.threadEvent,
  ORCHESTRATION_WS_CHANNELS.devServerStatusChanged,
]);
export type WsPushChannelSchema = typeof WsPushChannelSchema.Type;

export const WsPush = Schema.Union([
  WsPushServerWelcome,
  WsPushServerConfigUpdated,
  WsPushServerProviderStatusesUpdated,
  WsPushGitActionProgress,
  WsPushTerminalEvent,
  WsPushWorkspaceArchiveHookStatus,
  WsPushWorkspaceProjectScriptStatus,
  WsPushComposerQueueChanged,
  WsPushLocalExecRequest,
  WsPushLocalExecControl,
  WsPushOrchestrationDomainEvent,
  WsPushOrchestrationShellEvent,
  WsPushOrchestrationThreadEvent,
  WsPushOrchestrationDevServerStatusChanged,
]);
export type WsPush = typeof WsPush.Type;

export type WsPushMessage<C extends WsPushChannel> = Extract<WsPush, { channel: C }>;

export const WsPushEnvelopeBase = Schema.Struct({
  type: Schema.Literal("push"),
  sequence: WsPushSequence,
  channel: WsPushChannelSchema,
  data: Schema.Unknown,
});
export type WsPushEnvelopeBase = typeof WsPushEnvelopeBase.Type;

// ── Union of all server → client messages ─────────────────────────────

export const WsResponse = Schema.Union([WebSocketResponse, WsPush]);
export type WsResponse = typeof WsResponse.Type;
